import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/apiClient';
import TaskDetailsModal from './components/TaskDetailsModal';
import GanttChart from './components/GanttChart';
import { useProjectData } from './context/ProjectDataContext';
import useTaskOperations from './hooks/useTaskOperations';
import useComments from './hooks/useComments';
import { clampTaskDateUpdates } from '../../utils/fsClamp';
import { confirmProjectWindow } from '../../utils/projectWindow';
import { confirmClampAdjustments, alertImpossibleRange, alertEndBeforeStart } from '../../utils/clampFeedback';

// Extract links from custom_attributes_values (Link 1, Link Description 1, etc.)
const extractLinks = (customAttrs) => {
    if (!customAttrs) return [];
    const links = [];
    for (let i = 1; i <= 5; i++) {
        const url = customAttrs[`Link ${i}`];
        const desc = customAttrs[`Link Description ${i}`];
        if (url) {
            links.push({ url, description: desc || url });
        }
    }
    return links;
};

// Format date for display (UK format)
const formatDateUK = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const ClientTimeline = () => {
    const token = localStorage.getItem('clientToken');
    const leadId = localStorage.getItem('clientLeadId');
    const timestamp = localStorage.getItem('clientTimestamp');

    // Get all data from shared context
    const {
        loading,
        error,
        project,
        tasks,
        setTasks,
        columns,
        taskStatuses,
        teamSwimlaneId,
        epics,
        members,
        lead,
        weekendWork,
        links,
        invalidDeps,
        fetchProjectData,
        blockedTasksMap
    } = useProjectData();

    // Task CRUD and comments come from the same hooks the Kanban tab uses, so the
    // task modal behaves identically on both tabs (Bill, 2026-08-03)
    const {
        selectedTaskDetail,
        loadingTaskDetail,
        savingChanges,
        savingField,
        creatingSubtask,
        fetchTaskDetail,
        handleSaveChanges,
        handleDeleteTask,
        handleCreateSubtask,
        setSelectedTaskDetail
    } = useTaskOperations(
        token,
        leadId,
        timestamp,
        project,
        teamSwimlaneId,
        setTasks,
        fetchProjectData
    );

    const {
        addingComment,
        editingComment,
        deletingComment,
        handleAddComment,
        handleEditComment,
        handleDeleteComment
    } = useComments(token, leadId, timestamp, fetchTaskDetail);

    const [projectMembers, setProjectMembers] = useState([]);
    // Full-screen Gantt: page chrome hides, progress bar + chart fill the
    // viewport. Deliberately NOT persisted - reloading into full screen with
    // no visible way back is a trap (Bill, 2026-08-21).
    const [ganttFullScreen, setGanttFullScreen] = useState(false);

    // Fetch members when opening assignment editor
    const handleOpenAssignmentEditor = async () => {
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/project-members?leadId=${leadId}&timestamp=${timestamp}`
            );
            const data = await response.json();
            if (data.members) {
                setProjectMembers(data.members);
            }
        } catch (err) {
            console.error('Failed to fetch project members:', err);
        }
    };

    // Same trigger the Kanban tab uses: opening a task loads the member list the
    // assignment editor needs (Dashboard.js:324)
    useEffect(() => { if (selectedTaskDetail) handleOpenAssignmentEditor(); }, [selectedTaskDetail]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Listen for task open events from other components
    useEffect(() => {
        const handleOpenTask = (event) => {
            fetchTaskDetail(event.detail.taskId);
        };
        window.addEventListener('openTaskFromLinks', handleOpenTask);
        return () => window.removeEventListener('openTaskFromLinks', handleOpenTask);
    }, [fetchTaskDetail]);

    // Transform project tasks for the hierarchical GanttChart
    // Main tasks - don't have mainTaskId field
    const ganttTasks = (tasks || [])
        .filter(task => !task.mainTaskId)
        .map(task => ({
            taskId: task.taskId,
            text: task.subject,
            subject: task.subject,
            start: task.startDate || task.dueDate,
            end: task.dueDate,
            dueDate: task.dueDate,
            progress: task.progress || 0,
            statusId: task.epicId,
            isClosed: task.isClosed,
            swimlaneId: task.swimlaneId,
            // Carried so the resource grouping below can name an assignee at all
            assignedUsersNames: task.assignedUsersNames || []
        }));

    // Subtasks - have mainTaskId field set to parent ID
    const ganttSubtasks = (tasks || [])
        .filter(task => task.mainTaskId)
        .map(st => ({
            taskId: st.taskId,
            text: st.subject,
            subject: st.subject,
            start: st.startDate || st.dueDate,
            end: st.dueDate,
            dueDate: st.dueDate,
            progress: st.isClosed ? 100 : 0,
            status: st.statusName || st.status,
            statusName: st.statusName,
            isClosed: st.isClosed,
            mainTaskId: st.mainTaskId,
            parentId: st.mainTaskId,
            assignedToName: st.assignedToName,
            assignedTo: st.assignedTo,
            isMeeting: st.isMeeting || false,
            meetingType: st.meetingType || null,
            agenda: st.agenda || null,
            links: st.links || []
        }));

    // Handle task click
    const handleTaskClick = (taskId) => {
        fetchTaskDetail(taskId);
    };

    // Calculate progress stats from subtasks
    const totalSubtasks = ganttSubtasks.length;
    const completedSubtasks = ganttSubtasks.filter(t => t.isClosed).length;
    const progressPercent = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;

    // Calculate heatmap color (amber -> yellow -> green)
    const getProgressColor = (percent) => {
        if (percent <= 50) {
            const ratio = percent / 50;
            const r = Math.round(251 - (251 - 250) * ratio);
            const g = Math.round(146 + (204 - 146) * ratio);
            const b = Math.round(60 - (60 - 21) * ratio);
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            const ratio = (percent - 50) / 50;
            const r = Math.round(250 - (250 - 34) * ratio);
            const g = Math.round(204 - (204 - 197) * ratio);
            const b = Math.round(21 + (94 - 21) * ratio);
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    // Resource View Component (custom built)
    const ResourceView = () => {
        // Group tasks by assignee
        const tasksByAssignee = {};
        (members || []).forEach(m => {
            tasksByAssignee[m.name || m.email] = [];
        });
        tasksByAssignee['Unassigned'] = [];

        ganttTasks.forEach(task => {
            // Was assignedUsersFullName - a field written nowhere and stored in no
            // record, so every main task landed under 'Unassigned' (Bill, 2026-08-03)
            const assignee = task.assignedUsersNames?.[0] || 'Unassigned';
            if (!tasksByAssignee[assignee]) {
                tasksByAssignee[assignee] = [];
            }
            tasksByAssignee[assignee].push(task);
        });

        // Calculate date range (handle both startDate/dueDate and start/end field names)
        const allDates = ganttTasks.flatMap(t => [
            (t.startDate || t.start) ? new Date(t.startDate || t.start) : null,
            (t.dueDate || t.end) ? new Date(t.dueDate || t.end) : null
        ]).filter(Boolean);

        const minDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : new Date();
        const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) || 30;

        const getTaskPosition = (task) => {
            const start = (task.startDate || task.start) ? new Date(task.startDate || task.start) : minDate;
            const end = (task.dueDate || task.end) ? new Date(task.dueDate || task.end) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
            const left = ((start - minDate) / (1000 * 60 * 60 * 24) / totalDays) * 100;
            const width = (((end - start) / (1000 * 60 * 60 * 24) + 1) / totalDays) * 100;
            return { left: `${Math.max(0, left)}%`, width: `${Math.max(2, width)}%` };
        };

        const getProgressColour = (progress) => {
            if (progress >= 75) return 'bg-green-500';
            if (progress >= 50) return 'bg-yellow-500';
            if (progress >= 25) return 'bg-orange-500';
            return 'bg-red-500';
        };

        return (
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="border-b bg-gray-50 p-3">
                    <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">Resource View</span>
                        <span className="text-sm text-gray-500">
                            {formatDateUK(minDate)} - {formatDateUK(maxDate)}
                        </span>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    {Object.entries(tasksByAssignee)
                        .filter(([, tasks]) => tasks.length > 0)
                        .map(([name, personTasks]) => (
                            <div key={name} className="flex border-b hover:bg-gray-50">
                                <div className="w-48 flex-shrink-0 p-3 font-medium bg-gray-50 border-r">
                                    {name}
                                    <span className="text-xs text-gray-500 ml-2">({personTasks.length})</span>
                                </div>
                                <div className="flex-1 relative min-h-[60px] p-2">
                                    {personTasks.map((task, idx) => {
                                        const pos = getTaskPosition(task);
                                        return (
                                            <div
                                                key={task.taskId}
                                                className="absolute h-8 rounded cursor-pointer hover:opacity-80 flex items-center px-2 text-xs text-white overflow-hidden"
                                                style={{
                                                    left: pos.left,
                                                    width: pos.width,
                                                    top: `${idx * 36 + 4}px`,
                                                    backgroundColor: '#3b82f6'
                                                }}
                                                onClick={() => handleTaskClick(task.taskId)}
                                                title={`${task.subject || task.text} (${task.progress || 0}%)`}
                                            >
                                                <div
                                                    className={`absolute left-0 top-0 bottom-0 ${getProgressColour(task.progress || 0)} opacity-60`}
                                                    style={{ width: `${task.progress || 0}%` }}
                                                />
                                                <span className="relative z-10 truncate">{task.subject || task.text}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-gray-600">Loading Gantt...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col overflow-hidden ${ganttFullScreen ? 'fixed inset-0 z-[100] bg-white' : 'h-full'}`}>
            {/* Progress Bar */}
            {totalSubtasks > 0 && (
                <div className="w-full bg-gradient-to-b from-gray-50 to-gray-200 border-t border-b border-t-gray-100 border-b-gray-400 px-4 py-2 shadow-[0_3px_5px_rgba(0,0,0,0.15)]">
                    <div className="flex items-center gap-3">
                        <span className="text-[13px] text-gray-500 whitespace-nowrap">Overall Project Progress ({totalSubtasks - completedSubtasks} Outstanding, {completedSubtasks} Closed Tasks)</span>
                        <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                    width: `${progressPercent}%`,
                                    backgroundColor: getProgressColor(progressPercent),
                                    // Sheen over whatever colour the fill is:
                                    // lighter at the top, darker at the base
                                    backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(0,0,0,0.12))'
                                }}
                            />
                        </div>
                        <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{progressPercent}%</span>
                        {/* Today's date rides the progress strip: an existing row
                            below the header line, so it costs no extra height
                            (Bill, 2026-07-31) */}
                        <span className="text-[13px] text-gray-500 whitespace-nowrap ml-2">
                            {new Date().toLocaleDateString('en-GB', {
                                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                            })}
                        </span>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 overflow-hidden p-2">
                {invalidDeps?.length > 0 && (
                    <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 mb-2 rounded text-sm font-medium">
                        {invalidDeps.length} dependency reference{invalidDeps.length === 1 ? ' is' : 's are'} invalid and not shown. Please contact the Migration Experts team.
                    </div>
                )}
                <GanttChart
                    tasks={ganttTasks}
                    statuses={(project?.epics || []).map(e => ({ ...e, id: e.epicId }))}
                    epics={epics || []}
                    subtasks={ganttSubtasks}
                    links={links || []}
                    onTaskClick={handleTaskClick}
                    readOnly={true}
                    teamSwimlaneId={teamSwimlaneId}
                    weekendWork={weekendWork}
                    taskStatuses={project?.subtaskStatuses || []}
                    blockedTasksMap={blockedTasksMap}
                    // The project's real start. Without it the Gantt falls back
                    // to the earliest task date, so the Project Overview bar and
                    // the kick-off marker disagreed with the admin's view.
                    kickOffDate={project?.kickoffDate}
                    fullScreen={ganttFullScreen}
                    onToggleFullScreen={setGanttFullScreen}
                />
            </div>

            {/* Task Details Modal - same wiring and permissions as the Kanban tab */}
            <TaskDetailsModal
                task={selectedTaskDetail}
                isOpen={!!selectedTaskDetail}
                onClose={() => setSelectedTaskDetail(null)}
                onUpdate={(taskId, updates) => {
                    const { updates: clamped, adjustments, impossible, endBeforeStart } =
                        clampTaskDateUpdates(tasks, links, taskId, updates);
                    if (endBeforeStart) { alertEndBeforeStart(endBeforeStart); return; }
                    if (impossible) { alertImpossibleRange(impossible); return; }
                    if (!confirmClampAdjustments(adjustments)) return;
                    const { proceed, override } = confirmProjectWindow({
                        start: clamped.start,
                        dueDate: clamped.due_date || clamped.dueDate,
                        kickoffDate: project?.kickoffDate,
                        label: 'This task'
                    });
                    if (!proceed) return;
                    return handleSaveChanges(taskId, override ? { ...clamped, allowBeforeKickoff: true } : clamped);
                }}
                onDelete={handleDeleteTask}
                onCommentAdd={handleAddComment}
                onCommentEdit={handleEditComment}
                onCommentDelete={handleDeleteComment}
                onSubtaskCreate={(parentTaskId, subtaskData) => {
                    const { proceed, override } = confirmProjectWindow({
                        start: subtaskData.start,
                        dueDate: subtaskData.dueDate,
                        kickoffDate: project?.kickoffDate,
                        label: subtaskData.subject || 'This subtask'
                    });
                    if (!proceed) return;
                    return handleCreateSubtask(parentTaskId, override ? { ...subtaskData, allowBeforeKickoff: true } : subtaskData);
                }}
                onSubtaskClick={fetchTaskDetail}
                isLoading={loadingTaskDetail}
                projectName={project?.name}
                columns={columns}
                taskStatuses={taskStatuses}
                teamSwimlaneId={teamSwimlaneId}
                members={members || []}
                tasks={tasks || []}
                lead={lead}
                savingChanges={savingChanges}
                savingField={savingField}
                addingComment={addingComment}
                editingComment={editingComment}
                deletingComment={deletingComment}
                creatingSubtask={creatingSubtask}
                projectMembers={projectMembers}
                onOpenAssignmentEditor={handleOpenAssignmentEditor}
                readOnly={false}
                blockedTasksMap={blockedTasksMap}
            />
        </div>
    );
};

export default ClientTimeline;
